import { createApi, createDomain, createEvent, sample } from 'effector';
import produce, { type Draft } from 'immer';
import { createGate } from 'effector-react';
import memoizeOne from 'memoize-one';
import type { ECategoryMap, ESceneMap, GeneralID, SceneData } from '@types';
import { $categoryList, $categoryMap, $sceneMap } from '@stores/dataStores';
import { SMDndType } from './dnd';

const domain = createDomain('scenesManagementModal');

export interface ScenesManagementState {
  categoryPath: number[];
  sceneList: GeneralID[];
  selected: GeneralID[];
  query: string;
}

const initialData: ScenesManagementState = {
  categoryPath: [],
  sceneList: [],
  selected: [],
  query: '',
};

export const $scenesManagement =
  domain.store<ScenesManagementState>(initialData);

export const $scenesManagementApi = createApi($scenesManagement, {
  update: (
    state,
    updater: (
      draft: Draft<ScenesManagementState>
    ) => ScenesManagementState | void
  ) => {
    return produce(state, updater);
  },
  reset: () => initialData,
});

export const $GateSubCategoryDragging = createGate<boolean>({
  domain,
  defaultState: false,
});
export const $GateCategoryDragging = createGate<boolean>({
  domain,
  defaultState: false,
});

export const setGlobalDragging = createEvent<string | symbol | null>();
export const $isCategoryDragging = domain
  .store<boolean>(false)
  .on(setGlobalDragging, (state, payload) => {
    return payload === SMDndType.category;
  });
export const $isSubCategoryDragging = domain
  .store<boolean>(false)
  .on(setGlobalDragging, (state, payload) => {
    return payload === SMDndType.subCategory;
  });
export const $isScenesDragging = domain
  .store<boolean>(false)
  .on(setGlobalDragging, (state, payload) => {
    return payload === SMDndType.scenes;
  });

export type SMResultItem = SceneData & {
  categories: {
    id: number;
    name: string;
  }[];
};

export const $scenesManagementSearchResult = domain.store<SMResultItem[]>([]);
export const scenesManagementSearch = domain.event();

const getAllItems = memoizeOne(
  (cList: number[], cMap: ECategoryMap, sMap: ESceneMap) => {
    const result: SMResultItem[] = [];
    const fn = (
      idList: number[],
      resultArr: SMResultItem[],
      categories: {
        id: number;
        name: string;
      }[] = []
    ) => {
      idList.forEach((cId) => {
        const cItem = cMap.get(cId);
        if (!cItem) return;
        if (cItem.sceneIds.length) {
          cItem.sceneIds.forEach((sId) => {
            const sItem = sMap.get(sId);
            if (!sItem) return;
            resultArr.push({
              ...sItem,
              categories: [...categories, { id: cItem.id, name: cItem.name }],
            });
          });
        } else if (cItem.categoryIds.length) {
          fn(cItem.categoryIds, resultArr, [
            { id: cItem.id, name: cItem.name },
          ]);
        }
      });
    };
    fn(cList, result);
    return result;
  }
);

sample({
  source: {
    cList: $categoryList,
    cMap: $categoryMap,
    sMap: $sceneMap,
    query: $scenesManagement.map((s) => s.query),
  },
  fn: ({ cList, cMap, sMap, query }) => {
    if (!query) return $scenesManagementSearchResult.defaultState;
    const items = getAllItems(cList, cMap, sMap);
    return items.filter((x) =>
      x.name.toLowerCase().includes(query.toLowerCase())
    );
  },
  target: $scenesManagementSearchResult,
});
